import * as React from 'react'
import { FormattedMessage, FormattedHTMLMessage, defineMessages, useIntl } from 'react-intl'
import { useFormState } from 'react-use-form-state'
import Button from '../../../../components/button'
import FormCard from '../../../../components/form-card'
import { getValidity, Text, Select } from '../../../../components/form'

import GenericStep from '../generic-step'
import WhatYouGet from '../what-you-get'

import styles from './step-2.module.scss'
import { DooerComGetPublicSyncedPreOffer_publicAccountingOnboardingSyncedPreOffer } from './__generated__/DooerComGetPublicSyncedPreOffer'

import { AccountingOnboardingSyncedPreOfferBank } from '../../../../__generated__/globalTypes'

import { normalizeSvensktPersonnummer, validateSvensktPersonnummer } from '../../../../lib/utils'
import VideoPopup from '../../../../components/videopopup'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilm } from '@fortawesome/pro-solid-svg-icons'

interface IFormValues {
  bank:
    | ''
    | AccountingOnboardingSyncedPreOfferBank.swedbankCompany
    | AccountingOnboardingSyncedPreOfferBank.sebCompany
    | AccountingOnboardingSyncedPreOfferBank.handelsbankenCompany
    | AccountingOnboardingSyncedPreOfferBank.nordea
    | 'other'
  personnummer: string
}

interface IStep2Props {
  preOffer: DooerComGetPublicSyncedPreOffer_publicAccountingOnboardingSyncedPreOffer
  onComplete: (preOfferId: string, bank: string) => void
}

const messages = defineMessages({
  errorPersonnummer: {
    id: 'sync-bank.step-2.error-personnummer',
    defaultMessage: 'Ogiltligt personnummer',
  },
  errorBank: {
    id: 'sync-bank.step-2.error-bank',
    defaultMessage: 'Du måste välja en bank',
  },

  personnummer: {
    id: 'sync-bank.step-2.personnummer',
    defaultMessage: 'Personnummer',
  },
  bank: {
    id: 'sync-bank.step-2.bank',
    defaultMessage: 'Företags bank',
  },

  optionDefault: {
    id: 'sync-bank.step-2.bank-option-default',
    defaultMessage: 'Välj bank',
  },
  optionSwedbank: {
    id: 'sync-bank.step-2.bank-option-swedbank',
    defaultMessage: 'Swedbank',
  },
  optionHandelsbanken: {
    id: 'sync-bank.step-2.bank-option-handelsbanken',
    defaultMessage: 'Handelsbanken',
  },
  optionSeb: {
    id: 'sync-bank.step-2.bank-option-seb',
    defaultMessage: 'SEB',
  },
  optionNordea: {
    id: 'sync-bank.step-2.bank-option-nordea',
    defaultMessage: 'Nordea',
  },
  optionOther: {
    id: 'sync-bank.step-2.bank-option-other',
    defaultMessage: 'Annan bank',
  },
})

const Step2 = ({ onComplete, preOffer }: IStep2Props) => {
  // Use intl HOC
  const intl = useIntl()

  const [showVideoPopup, setVideoPopup] = React.useState(false)

  const errorMessages = {
    personnummer: intl.formatMessage(messages.errorPersonnummer),
    bank: intl.formatMessage(messages.errorBank),
  }
  const labelMessages = {
    personnummer: intl.formatMessage(messages.personnummer),
    bank: intl.formatMessage(messages.bank),
  }
  const bankOptions = [
    {
      text: intl.formatMessage(messages.optionDefault),
      value: '',
    },
    {
      text: intl.formatMessage(messages.optionSwedbank),
      value: 'swedbankCompany',
    },
    {
      text: intl.formatMessage(messages.optionSeb),
      value: 'sebCompany',
    },
    {
      text: intl.formatMessage(messages.optionHandelsbanken),
      value: 'handelsbankenCompany',
    },
    {
      text: intl.formatMessage(messages.optionNordea),
      value: 'nordea',
    },
    {
      text: intl.formatMessage(messages.optionOther),
      value: 'other',
    },
  ]

  const [formState, input] = useFormState<IFormValues>(
    {
      personnummer: preOffer.idNumber || '',
      bank: preOffer.bankInstitution || '',
    } as IFormValues,
    {
      withIds: true,
    }
  )

  const { bank } = formState.values

  return (
    <GenericStep
      content={
        <>
          {showVideoPopup && (
            <VideoPopup
              close={() => setVideoPopup(false)}
              video={{
                src: 'https://www.youtube.com/embed/5aa7oYH9PMQ?autoplay=1&rel=0&controls=0&showinfo=0',
                title: 'Onboarding - Hur fungerar det',
              }}
            />
          )}
          <FormCard
            button={
              <div className={styles.buttonWrapper}>
                <Button bankId block className={styles.cta} type="submit" variant="success">
                  <FormattedMessage defaultMessage="Synka" id="sync-bank.step-2.cta" />
                </Button>
              </div>
            }
            className={styles.card}
            col={2}
            extra={
              <div className={styles.formDescription}>
                <FormattedHTMLMessage
                  defaultMessage='Väljer du "Synka" så kommer vi att logga in på Skatteverket och din bank och ta fram informationen som vi behöver för att ge dig ett pris.'
                  id="sync-bank.step-2.form-description"
                  tagName="p"
                />
              </div>
            }
            inputs={[
              {
                node: (
                  <Text
                    errorCollapse="dynamic"
                    errorMessage={errorMessages.personnummer}
                    input={input}
                    label={labelMessages.personnummer}
                    name="personnummer"
                    pattern="\d*"
                    required
                    valid={getValidity(formState, 'personnummer')}
                    validate={(value: string) =>
                      validateSvensktPersonnummer(normalizeSvensktPersonnummer(value))
                        ? true
                        : errorMessages.personnummer
                    }
                    validateOnBlur
                  />
                ),
                key: 1,
              },
              {
                node: (
                  <Select
                    errorCollapse="dynamic"
                    errorMessage={errorMessages.bank}
                    input={input}
                    label={labelMessages.bank}
                    name="bank"
                    options={bankOptions.map((option) => ({
                      ...option,
                      selected: bank === option.value,
                    }))}
                    required
                    valid={getValidity(formState, 'bank')}
                  />
                ),
                key: 2,
              },
            ]}
            onSubmit={async (e) => {
              e.preventDefault()

              if (Object.keys(formState.errors).length > 0 || !preOffer) {
                return
              }

              onComplete(preOffer.organizationId, bank)
            }}
            style={{ width: '100%', maxWidth: '500px' }}
          />

          <div className={styles.reassurance}>
            <FormattedMessage
              defaultMessage="Efter det här steget så kommer du behöva BankID för att:"
              id="sync-bank.step-2.reassurance.heading-1"
              tagName="strong"
            />
            <ul>
              <FormattedMessage
                defaultMessage="Logga in på Skatteverket för ditt företag"
                id="sync-bank.step-2.reassurance.item-1"
                tagName="li"
              />
              <FormattedMessage
                defaultMessage="Logga in på din bank"
                id="sync-bank.step-2.reassurance.item-2"
                tagName="li"
              />
            </ul>
            <FormattedMessage
              defaultMessage="Varför vill ni komma åt min bank och Skatteverket?"
              id="sync-bank.step-2.reassurance.heading-2"
              tagName="strong"
            />
            <FormattedMessage
              defaultMessage="Istället för att ställa massa frågor till dig så kan vi få svar på dessa automatiskt genom att kolla på Skatteverket och din bank. Förutom att det är enklare vet vi även att information är korrekt."
              id="sync-bank.step-2.reassurance.item-3"
              tagName="p"
            />

            <Button onClick={() => setVideoPopup(true)} style={{ textAlign: 'left' }} variant="link">
              <i>
                <FontAwesomeIcon icon={faFilm} />
              </i>
              <FormattedMessage
                defaultMessage="Berätta mer om hur det fungerar"
                id="sync-bank.step-2.how-it-works-cta"
              />
            </Button>
          </div>
        </>
      }
      footer={<WhatYouGet />}
      header={
        <>
          <FormattedMessage defaultMessage="Analys av ditt företag" id="sync-bank.step-2.title" tagName="h1" />
          <FormattedMessage
            defaultMessage="Via den här guiden kommer vi hämta information om ditt företag från Skatteverket och din bank. Då kan vi göra en rättvis bedömning av ditt företag och ta fram ett pris som utgår ifrån din verksamhet."
            id="sync-bank.step-2.lede"
            tagName="p"
          />
        </>
      }
    />
  )
}

export default Step2
